import { useLayoutEffect, useRef, useState } from "react";

// Quantos "slots" de largura fixa cabem na sobra de uma linha flex — a conta que
// decide quais botões do cabeçalho ficam na barra e quais recolhem para o "...".
//
// POR QUE NÃO MEDIR OS PRÓPRIOS BOTÕES: esconder um botão muda a largura da
// barra, então "cabe / não cabe" medido a partir do que está renderizado oscila
// para sempre (esconde -> passa a caber -> mostra -> não cabe -> esconde...).
// Aqui a conta só depende de coisas que a nossa decisão NÃO altera: a largura da
// linha e a dos irmãos de largura fixa.
//
// PELO MESMO MOTIVO O ResizeObserver OBSERVA O PAI, NUNCA O PRÓPRIO ELEMENTO. A
// largura da linha é imposta de cima (o painel de conversa), e não muda quando
// escondemos um filho. Observar a si mesmo seria um laço garantido.
//
// Parâmetros:
//   slot                largura de um botão + suas margens, em px
//   reserve             piso em px do vizinho que encolhe (o bloco nome/avatar)
//   trailing            margem à direita da própria barra
//   shrinkableSelector  identifica esse vizinho dentro do pai
//   enabled             desliga a conta (devolve Infinity = mostra tudo)
//
// Devolve { ref, slots }. `ref` vai na raiz da barra. `slots` é Infinity
// enquanto não há medição, quando está desabilitado ou quando o navegador não
// tem ResizeObserver — ou seja, o padrão é MOSTRAR TUDO e nunca esconder por
// engano.
export const useToolbarOverflow = ({
  slot,
  reserve = 0,
  trailing = 0,
  shrinkableSelector = ".MuiCardHeader-root",
  enabled = true,
} = {}) => {
  const ref = useRef(null);
  const [slots, setSlots] = useState(Infinity);

  useLayoutEffect(() => {
    const el = ref.current;
    const parent = el && el.parentElement;

    if (!enabled || !parent || !slot || typeof ResizeObserver === "undefined") {
      setSlots(Infinity);
      return undefined;
    }

    let frame = null;

    const measure = () => {
      frame = null;

      const rowWidth = parent.clientWidth;
      // pai ainda sem layout (display:none, aba escondida): não decidir nada
      if (!rowWidth) return;

      const shrinkable = parent.querySelector(shrinkableSelector);

      // A linha quebrou (o flexWrap do breakpoint sm do cabeçalho): a barra
      // ganhou uma linha inteira só dela, então não há nome nenhum disputando
      // espaço e não se reserva nada. Detectado por verdade de layout, e não
      // por media query, porque o que importa é onde os elementos realmente
      // caíram. Os dois compartilham offsetParent, então dá para comparar.
      const wrapped = shrinkable ? el.offsetTop > shrinkable.offsetTop : false;

      let fixed = 0;
      if (!wrapped) {
        // Somar os irmãos de largura fixa em vez de assumir quem são: em
        // Ticket é só o botão de voltar, em ContactChat é voltar + a lupa
        // própria dele.
        for (let i = 0; i < parent.children.length; i += 1) {
          const child = parent.children[i];
          if (child === el || child === shrinkable) continue;

          const style = window.getComputedStyle(child);
          if (style.display === "none") continue;

          fixed +=
            child.offsetWidth +
            (parseFloat(style.marginLeft) || 0) +
            (parseFloat(style.marginRight) || 0);
        }
        fixed += reserve;
      }

      const next = Math.max(0, Math.floor((rowWidth - fixed - trailing) / slot));

      // Só re-renderiza quando o número inteiro de slots muda de verdade.
      // Abrir/fechar a gaveta de contato anima 320px de margem por ~195ms, e o
      // observer dispara a cada quadro; sem esta guarda seriam ~12 renders para
      // uma ou duas mudanças reais.
      setSlots((prev) => (prev === next ? prev : next));
    };

    // requestAnimationFrame tira a leitura de layout de dentro do callback do
    // observer — é o que evita o "ResizeObserver loop completed with
    // undelivered notifications" aparecer no overlay de erro do CRA em dev.
    const schedule = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(measure);
    };

    // primeira medição antes da pintura, para não piscar "tudo visível"
    measure();

    const observer = new ResizeObserver(schedule);
    observer.observe(parent);

    return () => {
      observer.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [enabled, slot, reserve, trailing, shrinkableSelector]);

  return { ref, slots };
};

export default useToolbarOverflow;
